"use client";

import { useEffect, useRef } from "react";
import { useCexPerpsSession } from "./cex-perps/useCexPerpsSession";
import { useExtendedSession } from "./extended/useExtendedSession";
import { useGrvtSession } from "./grvt/useGrvtSession";
import { useHibachiSession } from "./hibachi/useHibachiSession";
import { useHyperliquidSession } from "./hyperliquid/useHyperliquidSession";
import { useParadexSession } from "./paradex/useParadexSession";
import type { ExchangeSessionResult } from "./types";
import { useZeroOneSession } from "./zeroone/useZeroOneSession";
import type { AirdropFarmingTabId } from "@/constants/airdrop-farming";

export type { ConnectFormState, ExchangeConnection, ExchangeSessionResult } from "./types";

/** No-op session stub for CEX/spot exchanges that don't have a DEX session. */
const NO_OP_SESSION: ExchangeSessionResult = {
  connection: {
    approveAgent: async () => {},
    isApproving: false,
    approvalError: null,
    resetApprovalError: () => {},
    agentData: { agentAddress: "", accountId: undefined, agentName: undefined },
  },
  sessionStatus: "idle",
  accounts: [],
  selectedAccount: null,
  setSelectedAccount: () => {},
  handleSetupAction: () => {},
  isSettingUp: false,
  requiredWalletRole: null,
  requiredWalletAddress: undefined,
  exchangeAccountAddress: undefined,
  needsDeposit: false,
  preparationStep: null,
  setupLabel: undefined,
};

/**
 * Unified exchange session hook for airdrop farming.
 *
 * All per-exchange hooks are always called (React rules) but side-effects
 * only trigger for the active exchange. The result for the selected exchange
 * is returned.
 *
 * Adding a new exchange:
 * 1. Create a subdirectory under `hooks/airdrop-farming/<exchange>/`
 * 2. Add a `useNewExchangeSession` hook there
 * 3. Register it in the map below
 */
export function useExchangeSession(
  tabId: AirdropFarmingTabId,
  options?: { agentName?: string },
): ExchangeSessionResult {
  const sessionsByTab: Record<AirdropFarmingTabId, ExchangeSessionResult> = {
    hyperliquid: useHyperliquidSession({
      enabled: tabId === "hyperliquid",
      agentName: options?.agentName,
    }),
    paradex: useParadexSession(),
    grvt: useGrvtSession(),
    zeroone: useZeroOneSession(),
    extended: useExtendedSession(),
    hibachi: useHibachiSession(),
    bitget: useCexPerpsSession(),
  };

  const session: ExchangeSessionResult | undefined = sessionsByTab[tabId as AirdropFarmingTabId];

  // Auto-select single account when available (generic, applies to all exchanges)
  const autoSelectedRef = useRef(false);
  const prevTabIdRef = useRef(tabId);
  // Reset auto-selection guard when switching exchanges so the new exchange
  // can auto-select its single account independently.
  if (prevTabIdRef.current !== tabId) {
    prevTabIdRef.current = tabId;
    autoSelectedRef.current = false;
  }
  useEffect(() => {
    if (!session) return;
    if (session.accounts.length === 1 && !session.selectedAccount && !autoSelectedRef.current) {
      autoSelectedRef.current = true;
      session.setSelectedAccount(session.accounts[0] ?? null);
    }
    if (session.accounts.length !== 1) {
      autoSelectedRef.current = false;
    }
  }, [session?.accounts, session?.selectedAccount, session?.setSelectedAccount]);

  // For CEX/spot exchanges that don't have a DEX session, return a no-op stub
  if (!session) {
    return NO_OP_SESSION;
  }

  return session;
}
